import React, { useEffect, useRef, useState } from 'react'
import { Link as ScrollLink } from 'react-scroll'

import classNames from 'classnames'

import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useInPageNavContext } from '~/contexts/InPageNavContext'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import {
  getFirstLetter,
  getFirstWord,
  getLastWord,
  sortArrayKeyByFirstWord,
} from '~/utils/sortArrayKeyByFirstWord'
import { sortArrayKeyByLastWords } from '~/utils/sortArrayKeyByLastWord'

import styles from './ArtistsHeadCatalogue.module.css'

export type ArtistItem = {
  _id: string
  name: string
  totalArtworks?: number
  slug: string
  image?: any
}

export interface ArtistsHeadCatalogueProps {
  artistList?: ArtistItem[]
  orderBySurname?: boolean
  onLetterClick?: () => void
}

export default function ArtistsHeadCatalogue({
  artistList,
  orderBySurname = true,
  onLetterClick,
}: ArtistsHeadCatalogueProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)
  const sectionsRef = useRef<Array<HTMLElement | null>>([])

  const [activeLetter, setActiveLetter] = useState<string | null>(null)
  const { mainHeaderVisible } = useInPageNavContext()
  const [isManualScroll, setIsManualScroll] = useState(false)
  const lastScrollPosition = useRef(0)

  const [links, setLinks] = useState([])

  // Formulate grouping of artist items
  const sortedItems = orderBySurname
    ? sortArrayKeyByLastWords(artistList, 'name')
    : sortArrayKeyByFirstWord(artistList, 'name')

  const processedData: any = []

  let lastSectionIndex = 0

  // Process the data into the format we need.
  sortedItems?.map((item: { name: string }) => {
    // Get first letter of surname/alias
    const sectionFirstLetter = orderBySurname
      ? getFirstLetter(getLastWord(item.name))
      : getFirstLetter(getFirstWord(item.name))

    if (
      !processedData.some(function (obj: any) {
        return obj['section'] === sectionFirstLetter
      })
    ) {
      const itemsObj = [] as any

      sortedItems.map((item: { name: string }, idx: number) => {
        const itemFirstLetter = orderBySurname
          ? getFirstLetter(getLastWord(item.name))
          : getFirstLetter(getFirstWord(item.name))
        if (sectionFirstLetter === itemFirstLetter) {
          itemsObj.push({ ...item, index: idx + 1 })
        }
      })

      // New letter so create the section
      processedData[lastSectionIndex] = {
        section: sectionFirstLetter,
        items: itemsObj,
      }
      lastSectionIndex++
    }
  })

  const catalogueSections = [
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
  ]

  useEffect(() => {
    const elements: Array<HTMLElement | null> = []
    catalogueSections.forEach(letter => {
      const element = document.getElementById(`artists-${letter}`)
      if (element) {
        elements.push(element)
      }
    })

    sectionsRef.current = elements
  }, [catalogueSections])

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY

      // Check if it's a new scroll action
      if (Math.abs(scrollPosition - lastScrollPosition.current) > 5) {
        setIsManualScroll(false)
      }

      if (!isManualScroll) {
        const sectionOffsets = sectionsRef.current.map(section => {
          return section ? section.offsetTop : 0
        })

        let offsetCalculation = isMobile ? -110 : -230
        // TODO: expression was +, moved to =
        if (mainHeaderVisible) offsetCalculation = isMobile ? -160 : -70

        const currentSectionIndex = sectionOffsets.findIndex(
          (offset, index) =>
            scrollPosition - offsetCalculation >= offset &&
            scrollPosition - offsetCalculation <
              (sectionsRef.current[index + 1]?.offsetTop ?? Infinity)
        )

        if (
          currentSectionIndex !== -1 &&
          catalogueSections[currentSectionIndex]
        ) {
          setActiveLetter(catalogueSections[currentSectionIndex])
        }
      }

      lastScrollPosition.current = scrollPosition
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [catalogueSections, isMobile, isManualScroll])

  useEffect(() => {
    const newLinks: any = []
    catalogueSections.map(title => {
      const hasRef = processedData.findIndex(
        (section: any) => section.section.toLowerCase() === title.toLowerCase()
      )

      // const isActive = hasRef > -1

      newLinks.push(
        <ScrollLink
          key={title}
          to={`artists-${title.toLowerCase()}`}
          href={`#artists-${title.toLowerCase()}`}
          // aria-disabled={!isActive}
          smooth={true}
          offset={
            isMobile
              ? mainHeaderVisible
                ? -160
                : -110
              : mainHeaderVisible
                ? -250
                : -180
          }
          onClick={() => {
            setActiveLetter(title.toLowerCase())
            setIsManualScroll(true)
            onLetterClick?.()
          }}
          className={classNames(
            styles.catalogueLink,
            activeLetter &&
              activeLetter.toLowerCase() === title.toLowerCase() &&
              styles.active
          )}
        >
          <Text size={TextSizes.primaryTitleSml}>{title}</Text>
        </ScrollLink>
      )
    })
    setLinks(newLinks)
  }, [activeLetter, artistList, isMobile, onLetterClick])

  return <div className={styles.catalogueLinks}>{links.map(item => item)}</div>
}
